<template>
  <div id="candidate-gender-age">
    <vx-card title="Candidatos por genero y edad">
      <!-- LOADING -->
      <div
        v-if="uiState === 'loading'"
        class="gw-empty-state"
        style="height:350px;"
      >
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-empty-title">Cargando información…</div>
        <div class="gw-empty-subtitle">Un momento por favor</div>
      </div>

      <!-- READY (CHART) -->
      <VueApexCharts
        v-else-if="uiState === 'ready' && renderGraphic"
        type="bar"
        height="350"
        :options="chartOptions"
        :series="series"
      />

      <!-- EMPTY / ERROR -->
      <div
        v-else
        class="gw-empty-state"
        style="height:350px;"
      >
        <img
          class="gw-empty-img"
          :src="emptyStateImage"
          :alt="emptyStateAlt"
        />
        <div class="gw-empty-title">{{ emptyStateTitle }}</div>
        <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,
      requestKey: null,
      consultHandler: null,

      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      // Apex
      series: [
        { name: "", data: [] },
        { name: "", data: [] },
      ],

      chartOptions: {
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
          stackType: "100%",
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: "bottom",
                offsetX: -10,
                offsetY: 0,
              },
            },
          },
        ],
        colors: ["#5F0091", "#FFCF44"],
        xaxis: {
          categories: ["14-20", "21-25", "26-30", "31-35", "36-40", "41-50", "46-50"],
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: "right",
          offsetX: 0,
          offsetY: 50,
        },
      },
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },
    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },
    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },
    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      let path = `/metrics/candidate-gender-age?client_id=${this.userInfo.user.client_id}`;
      let url = "";

      if (this.startDate != null && this.endDate != null) {
        let params = `&start_date=${this.startDate}&end_date=${this.endDate}`;
        url = `${this.url()}${path}${params}`;
      } else {
        url = `${this.url()}${path}`;
      }

      if (this.cityId != null) url += `&city_id=${this.cityId}`;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [
        { name: "", data: [] },
        { name: "", data: [] },
      ];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    hasAnyValue(seriesArr) {
      if (!Array.isArray(seriesArr)) return false;
      for (let i = 0; i < seriesArr.length; i++) {
        const row = seriesArr[i];
        if (!Array.isArray(row)) continue;
        for (let j = 0; j < row.length; j++) {
          let v = row[j];
          if (typeof v === "string") v = parseFloat(v);
          if (typeof v === "number" && !isNaN(v) && v > 0) return true;
        }
      }
      return false;
    },

    async setReady(genders, seriesArr) {
      // force remount (evita glitches Apex)
      this.renderGraphic = false;
      await this.$nextTick();

      this.series = [
        { name: genders[0] || "", data: seriesArr[0] || [] },
        { name: genders[1] || "", data: seriesArr[1] || [] },
      ];

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      const key = `${this.startDate || ""}|${this.endDate || ""}|${this.cityId || ""}`;
      this.setLoading(key);

      console.log("[candidate-gender-age] initializeData:", {
        origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        const request = await this.requestGetWithoutContent(this.getUrl(), {});

        // race condition guard
        if (this.requestKey !== key) {
          console.log("[candidate-gender-age] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn("[candidate-gender-age] request not ok:", request && request.status);
          this.setError();
          return;
        }

        const data = request.data || {};

        const genders = data.genders;
        const seriesArr = data.series;

        // Validación de estructura mínima
        if (!Array.isArray(genders) || genders.length < 2) {
          console.warn("[candidate-gender-age] genders invalid:", genders);
          this.setEmpty();
          return;
        }

        if (!Array.isArray(seriesArr) || seriesArr.length < 2) {
          console.warn("[candidate-gender-age] series invalid:", seriesArr);
          this.setEmpty();
          return;
        }

        // Si todo está vacío/0 => empty
        if (!this.hasAnyValue(seriesArr)) {
          console.warn("[candidate-gender-age] series without positive values");
          this.setEmpty();
          return;
        }

        await this.setReady(genders, seriesArr);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[candidate-gender-age] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () => this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) eventBus.$off("consultData", this.consultHandler);
  },

  components: {
    VueApexCharts,
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
